import React, { useState } from 'react';
import { MB_Modal } from '@mightybyte/rnw.components.modal';
import { UserDataForAdmin } from '@sapphicsavvy/business';
import { StyleSheet, Text, View, TouchableOpacity } from 'react-native';
import { MB_Button } from '@mightybyte/rnw.components.button';
import { trpc } from '../../../../apiCalls/trpcClient';
import { mbApplyTransparency, mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { textStyles } from '../../../../constants/textStyles';
import { Spacer } from '../../../helperComponents/Spacer';
import { utils } from '../../../../utils/utils';
import GameControlIcon  from '../../../../resources/svgComponents/GameControlIcon';
import ShadowCheckbox from '../../../helperComponents/ShadowCheckbox';

const POINT_OPTIONS = [75, 150, 225];

export default function AddPointsModal({ isVisible, userInfo, onDismiss }: { isVisible: boolean, userInfo?: UserDataForAdmin, onDismiss?: () => void }) {
  const [selectedPoints, setSelectedPoints] = useState<number | null>(null);
  const [addBadge, setAddBadge] = useState<boolean>(false);
  const { mutate: addPoints, isLoading } = trpc.admin.users.addPoints.useMutation();

  const onSubmit = () => {
    if (!userInfo || !selectedPoints) return;
    addPoints(
      { userId: userInfo.id, amount: selectedPoints, addBadge },
      {
        onSuccess: (data) => {
          onDismiss?.();
          setSelectedPoints(null);
          setAddBadge(false);
          setTimeout(() => {
            const badgeMessage = addBadge ? ' Mini game winner badge added.' : '';
            utils.showToast('success', `Successfully added ${selectedPoints} points to ${userInfo.userName}. Total points: ${data.totalPoints}.${badgeMessage}`);
          }, 500);
        },
        onError: (error) => {
          utils.showToast('error', error.message || 'Failed to add points');
        },
      }
    );
  };

  const handleDismiss = () => {
    setSelectedPoints(null);
    setAddBadge(false);
    onDismiss?.();
  };

  return (
    <MB_Modal
      isVisible={isVisible}
      onDismiss={handleDismiss}
      hideCloseButton
      childrenWrapperStyle={styles.container}
    >
      <View style={styles.headerIcon}>
        <GameControlIcon size={28} />
      </View>
      <Spacer height={10} />
      <View>
        <Text style={styles.titleText}>Add Mini Game Points to User</Text>
        <Spacer height={20} />
        <View style={styles.user}>
          <Text style={styles.userText}>{userInfo?.userName}</Text>
        </View>
        <Spacer height={20} />
        <Text style={styles.noteText}>Select the number of points to add:</Text>
        <Spacer height={16} />
        <View style={styles.pointsOptionsContainer}>
          {POINT_OPTIONS.map((points) => (
            <TouchableOpacity
              key={points}
              style={[
                styles.pointOption,
                selectedPoints === points && styles.pointOptionSelected,
              ]}
              onPress={() => setSelectedPoints(points)}
            >
              <Text
                style={[
                  styles.pointOptionText,
                  selectedPoints === points && styles.pointOptionTextSelected,
                ]}
              >
                {points} points
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        <Spacer height={20} />
        <ShadowCheckbox
          value={addBadge}
          onChange={() => setAddBadge(!addBadge)}
          text="Add Mini game winner badge"
          textStyle={styles.noteText}
        />
      </View>
      <Spacer height={10} />
      <View style={styles.footer}>
        <MB_Button
          title="Add Points"
          style={styles.primaryBtn}
          textStyle={styles.primaryBtnText}
          onPress={onSubmit}
          disabled={isLoading || !selectedPoints}
          loading={isLoading}
        />
        <MB_Button
          title="Cancel"
          style={styles.secondaryBtn}
          textStyle={[styles.primaryBtnText, styles.secondaryBtnText]}
          onPress={handleDismiss}
        />
      </View>
    </MB_Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 409,
    paddingTop: 20,
    paddingBottom: 0,
    paddingHorizontal: 20,
    alignItems: 'stretch',
  },
  headerIcon: {
    alignSelf: 'center',
    width: 61,
    height: 61,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: mbApplyTransparency('#F35E2E', 0.13),
    borderRadius: 360,
  },
  titleText: mbTextStyles([textStyles.largeText, {
    fontSize: 21,
    color: '#000000',
    fontWeight: '600',
  }]),
  user: {
    backgroundColor: '#F5F7F7',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 17,
  },
  userText: mbTextStyles([textStyles.normalText, {
    color: '#000000',
    fontWeight: '500',
  }]),
  noteText: mbTextStyles([textStyles.normalText, {
    color: '#000000',
    fontWeight: '400',
  }]),
  pointsOptionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  pointOption: {
    flex: 1,
    paddingVertical: 16,
    paddingHorizontal: 12,
    backgroundColor: '#F5F7F7',
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#E0E0E0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pointOptionSelected: {
    backgroundColor: mbApplyTransparency('#F35E2E', 0.1),
    borderColor: '#F35E2E',
  },
  pointOptionText: mbTextStyles([textStyles.normalText, {
    color: '#666666',
    fontWeight: '600',
    fontSize: 16,
  }]),
  pointOptionTextSelected: {
    color: '#F35E2E',
  },
  footer: {
    height: 95,
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FEF2EE',
    paddingHorizontal: 30,
    flexDirection: 'row',
    marginHorizontal: -20,
  },
  primaryBtn: {
    width: 167,
    height: 49,
    backgroundColor: '#F35E2E',
    borderRadius: 8,
    borderWidth: 0,
  },
  primaryBtnText: mbTextStyles([textStyles.normalText, {
    textAlign: 'left',
    color: '#FFFFFF',
  }]),
  secondaryBtn: {
    width: 167,
    height: 49,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: '#F35E2E',
  },
  secondaryBtnText: {
    color: '#F35E2E',
  },
});
